import React, { useCallback, useState } from 'react';

import styled from 'styled-components';

import CloseIcon from 'common/Icons/CloseIcon';
import Button from 'components/lib/Button';
import { CommunityType } from 'types/communities';

import OptionsCheckboxes from './ShopRequestModalCheckboxes';
import { useShopRequestModalContext } from './ShopRequestModalContext';
import ShopRequestModalDropdown from './ShopRequestModalDropdown';
import ShopRequestsModalPropagation from './ShopRequestsModalPropagation';
import { SHOP_REQUEST_TYPE } from '../constants';
import EditNoteButton from '../NoteButton/EditNoteButton';
import { ShopRequestCreationObject } from '../types';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const PricingContainer = styled(Container)`
  margin-bottom: 20px;
`;

const Controls = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  .note-button-container {
    opacity: 0;
  }
  .shop-request-add-note {
    opacity: 1;
  }
  :hover {
    .note-button-container {
      opacity: 1;
    }
  }
`;

const CloseButton = styled(Button)`
  padding: 0;
  height: 16px;
  width: 16px;
  border: 0;
  margin-left: 20px;
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;
  outline: none;
`;

type Props = {
  shopRequest: ShopRequestCreationObject;
  community?: CommunityType | null;
  remainingCompetitors?: CommunityType[];
  loading?: boolean;
  disabled?: boolean;
};

const ShopRequestModalCommunityRow = ({
  shopRequest,
  community,
  remainingCompetitors,
  loading = false,
  disabled = false,
}: Props) => {
  const { shopRequests, requestType, setShopRequests, deleteCommunity, replaceCommunity } =
    useShopRequestModalContext();
  const [isModalOpen, setModalOpen] = useState(false);
  const currentCommunity =
    community ??
    ({
      id: shopRequest.communityId,
      comunity_name: shopRequest.communityName,
    } as CommunityType);
  const RowContainer = requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? PricingContainer : Container;

  const toggleShopRequestProperty = useCallback(
    (checked: boolean, property: 'independentLiving' | 'assistedLiving' | 'memoryCare') => {
      const updatedShopRequests = [...shopRequests];
      updatedShopRequests[updatedShopRequests.indexOf(shopRequest)][property] = checked;
      setShopRequests(updatedShopRequests);
    },
    [shopRequests, setShopRequests]
  );

  const toggleCommunityIndependentLiving = useCallback(
    (checked: boolean) => {
      toggleShopRequestProperty(checked, 'independentLiving');
    },
    [toggleShopRequestProperty]
  );

  const toggleCommunityAssistedLiving = useCallback(
    (checked: boolean) => {
      toggleShopRequestProperty(checked, 'assistedLiving');
    },
    [toggleShopRequestProperty]
  );

  const toggleCommunityMemoryCare = useCallback(
    (checked: boolean) => {
      toggleShopRequestProperty(checked, 'memoryCare');
    },
    [toggleShopRequestProperty]
  );

  const updateAdminNote = useCallback(
    (note: string) => {
      const modifiedShopRequests = [...shopRequests];
      modifiedShopRequests[shopRequests.indexOf(shopRequest)].adminNote = note;
      setShopRequests(modifiedShopRequests);
    },
    [shopRequest, shopRequests, setShopRequests]
  );

  return (
    <RowContainer>
      <Controls>
        <ShopRequestModalDropdown
          communities={[currentCommunity, ...(remainingCompetitors ?? [])].filter(Boolean)}
          onChange={(newCommunity: any) => replaceCommunity(currentCommunity, newCommunity)}
          value={currentCommunity?.id}
          loading={loading}
          disabled={disabled}
          width={requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP ? '480px' : '100%'}
        />
        {requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP && (
          <OptionsCheckboxes
            independentLiving={!!shopRequest.independentLiving}
            assistedLiving={!!shopRequest.assistedLiving}
            memoryCare={!!shopRequest.memoryCare}
            toggleIndependentLiving={toggleCommunityIndependentLiving}
            toggleAssistedLiving={toggleCommunityAssistedLiving}
            toggleMemoryCare={toggleCommunityMemoryCare}
          />
        )}
        {requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP && (
          <EditNoteButton
            isModalOpen={isModalOpen}
            note={shopRequest.adminNote}
            setModalOpen={setModalOpen}
            onCloseModal={() => setModalOpen(false)}
            modifyNote={updateAdminNote}
          />
        )}
        <CloseButton onClick={() => deleteCommunity(currentCommunity?.id ?? shopRequest.communityId)}>
          <CloseIcon size={16} alt="add" />
        </CloseButton>
      </Controls>
      {requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP && (
        <ShopRequestsModalPropagation community={currentCommunity} shopRequest={shopRequest} />
      )}
    </RowContainer>
  );
};

export default ShopRequestModalCommunityRow;
